import React from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LayersIcon from '@mui/icons-material/Layers';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ETVTable from "../tools/ETVTable.js";
import FloorMgmt from './FloorMgmt.js';
import * as ETVConstants from '../ETVConstants.js';
import ETVPage from '../tools/ETVPage.js';
import ETVPopup from '../tools/ETVPopup.js';
import ETVMessagePopup from '../tools/ETVMessagePopup.js';
import ETVActionButton from '../tools/ETVActionButton.js';
import { createOverallFloorDirectoryHtml } from './FloorDirectoryHtml.js';
import DirectoryFormatPicker from './DirectoryFormatPicker.js';

class AccessMgmt extends React.Component {
  floorMgmtRef = React.createRef();

  state = {
    items: [],
    isLoaded: false,
    isLoading: false,
    selected_location_id: 0,
    window: '',
    info_title: '',
    info_message: '',
    error: null,
    directory_floors: [],
    directory_active_format: '',
    directory_style: 'premium',
    directory_loading: false,
    directory_generating: false,
    directory_error: '',
  };

  componentDidMount() {
    this.loadLocations();
  }

  componentDidUpdate() {
    if (!this.state.isLoaded && !this.state.isLoading) {
      this.loadLocations();
    }
  }

  loadLocations = () => {
    this.setState({ isLoading: true, error: null });
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_locations&user_id=${this.props.user_id}`;

    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          this.setState({
            items: result.list || [],
            isLoaded: true,
            isLoading: false,
          });
        },
        (error) => {
          this.setState({
            items: [],
            isLoaded: false,
            isLoading: false,
            error,
          });
        }
      );
  };

  refresh = () => this.setState({ isLoaded: false }, this.loadLocations);

  showWindow = (divID, show) => this.setState({ window: show ? divID : '' });

  showInfoMessage = (title, msg) => {
    this.setState({ window: 'message_div', info_title: title, info_message: msg });
  };

  closeInfoMessage = () => this.setState({ window: '', info_message: undefined, info_title: '' });

  editFloors = (locationID) => {
    this.setState({ window: 'edit_floor_div', selected_location_id: locationID });
  };

  createFloorDirectory = (locationID) => {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/floor_access?action=get_floors&location_id=${locationID}`;
    this.setState({
      window: 'directory_options',
      selected_location_id: locationID,
      directory_floors: [],
      directory_loading: true,
      directory_error: '',
    });
    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then(res => res.json())
      .then(result => {
        if (result?.result === false) throw new Error(result.data || 'Failed to load floors');
        this.setState({ directory_floors: result.list || [], directory_loading: false });
      })
      .catch(error => this.setState({
        directory_loading: false,
        directory_error: error.message || String(error),
      }));
  };

  downloadFloorDirectory = async (format) => {
    this.setState({ directory_generating: true, directory_active_format: format, directory_error: '' });
    try {
      const html = await createOverallFloorDirectoryHtml(
        this.state.directory_floors,
        format,
        this.state.directory_style
      );
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'floor-directory-all.html';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.setState({ window: '', directory_generating: false, directory_active_format: '' });
    } catch (error) {
      this.setState({
        directory_generating: false,
        directory_active_format: '',
        directory_error: error.message || ETVConstants.trans('floor_directory_generation_error'),
      });
    }
  };

  render() {
    const { items, isLoading, error } = this.state;

    const columnsDataLocation = [
      {
        title: 'Building', field: 'name',
        render: rd => <Typography variant="subtitle2" fontWeight={700} noWrap title={rd.name} sx={{minWidth:180}}>{rd.name || '-'}</Typography>
      },
      {
        title: ETVConstants.trans('address'), field: 'address',
        render: rd => <Box sx={{display:'flex',gap:1,minWidth:260,alignItems:'flex-start'}}><PlaceOutlinedIcon fontSize="small" color="action" sx={{mt:0.2}}/><Box><Typography variant="body2">{rd.address || '-'}</Typography><Typography variant="caption" color="text.secondary">{[rd.zip,rd.city,rd.country].filter(Boolean).join(' · ') || '-'}</Typography></Box></Box>
      },
      {
        title: ETVConstants.trans('floors'),
        field: '',
        render: rowData => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<LayersIcon />}
              onClick={() => this.editFloors(rowData.location_id)}
            >
              Manage floors
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<ImageOutlinedIcon />}
              onClick={() => this.createFloorDirectory(rowData.location_id)}
            >
              {ETVConstants.trans('create_directory_image') || 'Create floor directory'}
            </Button>
          </Box>
        ),
      },
    ];

    const sortedItems=[...(items || [])].sort((a,b)=>String(a.name || '').localeCompare(String(b.name || ''),undefined,{numeric:true,sensitivity:'base'}));
    return (
      <ETVPage width="standard" icon={<LocationCityIcon fontSize="large"/>} title="Buildings" description="View your buildings and manage their floor definitions."
        loading={isLoading}>
            {error && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">
                  {ETVConstants.trans('error') || 'Error'}: {String(error)}
                </Alert>
              </Box>
            )}

            <Box sx={{width:'100%'}}>
              <ETVTable
                data={sortedItems}
                columns={columnsDataLocation}
                title={""}
                pageSize={20}
                search={true}
              />
            </Box>

        {/* Info dialog */}
        <ETVMessagePopup
          open={this.state.window === 'message_div'}
          onClose={this.closeInfoMessage}
          type="info" title={this.state.info_title} message={this.state.info_message}/>

        <ETVPopup
          open={this.state.window === 'directory_options'}
          onClose={() => {
            if (!this.state.directory_generating) this.setState({ window: '' });
          }}
          width="small"
          icon={<ImageOutlinedIcon />}
          title={ETVConstants.trans('create_directory_image') || 'Create floor directory'}
          description={ETVConstants.trans('choose_directory_format')}
          loading={this.state.directory_loading}
          bottomActions={(
            <>
              <ETVActionButton
                action="cancel"
                disabled={this.state.directory_generating}
                onClick={() => this.setState({ window: '' })}
              />
            </>
          )}
        >
          <Box sx={{ display: 'grid', gap: 2 }}>
            {this.state.directory_error && <Alert severity="error">{this.state.directory_error}</Alert>}
            <DirectoryFormatPicker
              disabled={this.state.directory_loading || this.state.directory_generating || !!this.state.directory_error}
              activeFormat={this.state.directory_active_format}
              directoryStyle={this.state.directory_style}
              onStyleChange={(directory_style) => this.setState({ directory_style })}
              onSelect={this.downloadFloorDirectory}
            />
          </Box>
        </ETVPopup>

        {/* Floors dialog */}
        <ETVPopup
          open={this.state.window === 'edit_floor_div'}
          width="wide" icon={<LayersIcon/>} title={ETVConstants.trans('floors') || 'Floors'}
          description="Add, edit, and organize the floors for this building."
          onClose={() => this.setState({ window: '' })}
          topActions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => this.floorMgmtRef.current?.addFloor()}
            >
              {ETVConstants.trans('add_floor') || 'Add floor'}
            </Button>
          }
          bottomActions={<ETVActionButton action="cancel" label="Close" onClick={()=>this.setState({window:''})}/>}
        >
            {this.state.selected_location_id ? (
              <FloorMgmt ref={this.floorMgmtRef} location_id={this.state.selected_location_id} />
            ) : null}
        </ETVPopup>
      </ETVPage>
    );
  }
}

export default AccessMgmt;
